import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Card } from '@components/ui/Card'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import { motion } from 'framer-motion'
import { Gift, Loader2, AlertCircle } from 'lucide-react'
import { useAuth } from '@contexts/AuthContext'

export default function TrainingRedeem() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, signIn } = useAuth()

  // Email from URL param or user input
  const emailFromUrl = searchParams.get('email') || ''
  const [email, setEmail] = useState(emailFromUrl)

  // States
  const [step, setStep] = useState('check') // 'check', 'found', 'create-account', 'sign-in', 'success'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [giftData, setGiftData] = useState(null)

  // Account creation
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')

  // Sign in
  const [signInPassword, setSignInPassword] = useState('')

  // Check if user already has access when logged in
  useEffect(() => {
    if (user?.email) {
      checkAccessForUser(user.email)
    }
  }, [user])

  const checkAccessForUser = async (userEmail) => {
    try {
      const response = await fetch('/api/training/check-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, userId: user?.id })
      })
      const data = await response.json()

      if (data.hasAccess) {
        // Already have access, redirect to training
        navigate('/training/photography-fundamentals')
      }
    } catch (err) {
      console.error('Error checking access:', err)
    }
  }

  const handleCheckGift = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      // Check if there's a gift purchase for this email
      const response = await fetch('/api/training/check-gift', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to check gift status')
      }

      if (!data.hasGift) {
        setError('No gift found for this email address. Please check the email and try again.')
        return
      }

      setGiftData(data.gift)

      // Check if user exists
      if (data.userExists) {
        if (data.hasAuth) {
          // User exists with password - sign in
          setStep('sign-in')
        } else {
          // User exists but needs password (created via express checkout)
          setStep('create-account')
        }
      } else {
        // New user - create account
        setStep('create-account')
      }
    } catch (err) {
      console.error('Error checking gift:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAccount = async (e) => {
    e.preventDefault()
    setError(null)

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      // Create account or set password
      const response = await fetch('/api/training/redeem-gift', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          fullName: fullName || 'Course Student',
          purchaseId: giftData.id
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create account')
      }

      // Sign in
      const { error: signInError } = await signIn(email, password, { skipRedirect: true })

      if (signInError) {
        console.error('Auto sign-in failed:', signInError)
        setError('Account created! Please sign in manually.')
        setStep('sign-in')
        return
      }

      // Navigate to training
      navigate('/training/photography-fundamentals')
    } catch (err) {
      console.error('Error creating account:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSignIn = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const { error: signInError } = await signIn(email, signInPassword, { skipRedirect: true })

      if (signInError) {
        throw new Error('Invalid password. Please try again.')
      }

      // Navigate to training
      navigate('/training/photography-fundamentals')
    } catch (err) {
      console.error('Sign in error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef2f4] to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Card className="p-5 sm:p-8">
          {/* Header */}
          <div className="text-center mb-4 sm:mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-[#fef2f4] rounded-full mb-3 sm:mb-4"
            >
              <Gift className="w-6 h-6 sm:w-8 sm:h-8 text-[#fe395f]" />
            </motion.div>

            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Redeem Your Gift
            </h1>
            <p className="text-sm sm:text-base text-[#606b7c]">
              {step === 'check' && "Enter your email to claim your Photography Fundamentals course access."}
              {step === 'found' && "We found your gift! Let's get you set up."}
              {step === 'create-account' && "Create your account to access the course."}
              {step === 'sign-in' && "Sign in to access your gifted course."}
            </p>
          </div>

          {/* Gift info banner */}
          {giftData && (
            <div className="bg-[#fef2f4] border border-[#fe395f]/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-start gap-2 sm:gap-3">
                <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-[#fe395f] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900 text-sm sm:text-base">
                    Photography Fundamentals Course
                  </p>
                  {giftData.gift_message && (
                    <p className="text-xs sm:text-sm text-[#606b7c] mt-1 italic">
                      "{giftData.gift_message}"
                    </p>
                  )}
                  <p className="text-xs text-[#606b7c] mt-1">
                    From: {giftData.purchaser_email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="mb-4 sm:mb-6 p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs sm:text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step: Check email */}
          {step === 'check' && (
            <form onSubmit={handleCheckGift} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter the email the gift was sent to"
                  required
                  className="text-sm"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90 text-sm sm:text-base"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Looking up gift...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    Redeem Gift
                  </div>
                )}
              </Button>
            </form>
          )}

          {/* Step: Create account */}
          {step === 'create-account' && (
            <form onSubmit={handleCreateAccount} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  value={email}
                  disabled
                  className="bg-gray-50 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <Input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  className="text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  minLength={6}
                  required
                  className="text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="text-sm"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90 text-sm sm:text-base"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Account...
                  </div>
                ) : (
                  'Create Account & Access Course'
                )}
              </Button>
            </form>
          )}

          {/* Step: Sign in */}
          {step === 'sign-in' && (
            <form onSubmit={handleSignIn} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  value={email}
                  disabled
                  className="bg-gray-50 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <Input
                  type="password"
                  value={signInPassword}
                  onChange={(e) => setSignInPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="text-sm"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90 text-sm sm:text-base"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing In...
                  </div>
                ) : (
                  'Sign In & Access Course'
                )}
              </Button>

              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="w-full text-xs sm:text-sm text-[#606b7c] hover:text-[#fe395f]"
              >
                Forgot your password?
              </button>
            </form>
          )}

          {/* Back to purchase link */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 text-center">
            <p className="text-xs sm:text-sm text-[#606b7c]">
              Don't have a gift?{' '}
              <button
                onClick={() => navigate('/training/photography-fundamentals/purchase')}
                className="text-[#fe395f] hover:underline font-medium"
              >
                Purchase the course
              </button>
            </p>
          </div>
        </Card>
      </motion.div>
    </div>
  )
}
